import React, { memo, useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTheme, createUseStyles, Loader, Icon, Text, Select } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';
import { useWeb5Context } from 'lib/Web5Context';
import UploadImageCarousel from '../../../components/UploadImageCarousel';
import {
    getImages,
    deleteImage,
    uploadImage,
    requestFormImageUpload,
    getFormRecordId,
} from '../../../lib/services/forms';
import { normalizeFormDocumentImages } from 'utils/normalizeFormDocumentImages';
import { errorToast } from 'utils/toast';

import styles from './styles';
const useStyles = createUseStyles(styles, 'FormsModal');

const getUploadErrorText = (error, fallback) =>
    error?.response?.data?.message ??
    error?.response?.data?.error ??
    error?.message ??
    (error?.response?.status ? `${fallback} (HTTP ${error.response.status})` : fallback);

function FormModal({
    formTemplates,
    form,
    className,
    carouselFullModalClass,
    showImages,
    expandImages,
    setExpandImages,
    isLoading,
    ...props
}) {
    const theme = useTheme();
    const classes = useStyles();
    const { t, bridgeBackend } = useWeb5Context();
    const [template, setTemplate] = useState(null);
    const [formTypes, setFormTypes] = useState(null);
    const [images, setImages] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const imagesRequestRef = useRef(0);

    // Classes
    const rootClassNames = classnames(classes.root, className);
    const carouselFullModalClassNames = classnames(
        classes.carouselFullModal,
        carouselFullModalClass,
    );

    const refreshImages = useCallback(async (formId) => {
        const requestId = ++imagesRequestRef.current;
        const { data } = await getImages(formId);

        if (requestId !== imagesRequestRef.current) {
            return;
        }

        setImages(normalizeFormDocumentImages(data));
    }, []);

    useEffect(() => {
        const formId = getFormRecordId(form);

        if (!formId) {
            setImages([]);
            return;
        }

        const requestId = ++imagesRequestRef.current;
        setImages(null);

        getImages(formId)
            .then((res) => {
                if (requestId !== imagesRequestRef.current) return;
                setImages(normalizeFormDocumentImages(res.data));
            })
            .catch((err) => {
                console.error('[FormModal] Failed to load form images', err);
                if (requestId !== imagesRequestRef.current) return;
                setImages([]);
            });
    }, [form?.id, form?.Id]);

    const onAddImage = useCallback(
        async (droppedImages) => {
            if (!droppedImages?.length) return;

            let formId = getFormRecordId(form);

            if (!formId) {
                try {
                    const { form: bridgeForm } = await bridgeBackend.getContext();
                    formId = getFormRecordId(bridgeForm);
                } catch (err) {
                    console.error('[FormModal] Failed to resolve form id from bridge context', err);
                }
            }

            if (!formId) {
                return;
            }

            let lastError = null;

            await Promise.all(
                droppedImages.map(async (image) => {
                    try {
                        const { id, locator } = await requestFormImageUpload(formId, image.name);
                        await uploadImage(locator, image);
                        return { id, image };
                    } catch (err) {
                        lastError = err;
                        console.error('[FormModal] Failed to upload form image', {
                            formId,
                            fileName: image.name,
                            error: err,
                        });
                        return null;
                    }
                }),
            ).then(async (results) => {
                const uploaded = results.filter(Boolean);

                if (!uploaded.length) {
                    errorToast({
                        t,
                        title: t('title_warning'),
                        text: getUploadErrorText(lastError, t('error_an_error_occurred')),
                    });
                    return;
                }

                try {
                    await refreshImages(formId);
                } catch (err) {
                    console.error('[FormModal] Upload succeeded but image refresh failed', err);
                    imagesRequestRef.current += 1;
                    setImages((prev) => [
                        ...(prev ?? []),
                        ...uploaded.map(({ id, image }) => ({
                            id,
                            description: image.name,
                            strLink: URL.createObjectURL(image),
                        })),
                    ]);
                }
            });
        },
        [bridgeBackend, form, refreshImages, t],
    );

    const onDeleteImage = useCallback(
        (index) => {
            const imageList = images ?? [];
            if (!imageList[index]?.id) return;

            deleteImage(imageList[index].id).then((res) => {
                if (res.status === 200) {
                    const filteredImages = [...imageList];
                    filteredImages.splice(index, 1);
                    setImages(filteredImages);
                } else {
                    console.warn('Delete image error');
                }
            });
        },
        [images],
    );

    useEffect(() => {
        if (template || !formTemplates) return;
        if (form?.formTemplateId && formTemplates[form.formTemplateId]) {
            return setTemplate(formTemplates[form.formTemplateId]);
        }
        const formTypes = Object.keys(formTemplates)
            .filter((key) => formTemplates[key].z_campoextra3 === -1)
            .map((key) => ({
                ...formTemplates[key],
                label: formTemplates[key].description,
                value: formTemplates[key].id,
            }));
        if (formTypes.length === 1) {
            return setTemplate(formTypes[0]);
        }
        setFormTypes(formTypes);
    }, [form, formTemplates, template]);

    useEffect(() => {
        if (template?.guid && !loaded) {
            bridgeBackend
                .getContext()
                .then(({ form }) =>
                    bridgeBackend.setContext({
                        form: {
                            ...form,
                            formTemplate: template,
                        },
                    }),
                )
                .then(() =>
                    bridgeBackend.loadFragment(
                        template.guid.toUpperCase(),
                        template.url,
                        `fm-fragment-${template.guid.toUpperCase()}`,
                    ),
                )
                .then(() => setLoaded(true))
                .catch((err) => {
                    if (err.response?.data?.code === 100) {
                        console.warn('Invalid fragment GUID');
                    }
                    console.warn(err);
                    setError(true);
                });
        }
    }, [bridgeBackend, form, formTemplates, loaded, template]);

    // const onChangeFormType = useCallback((formType) => setTemplate(formType), []);

    if (!form) return null;

    if (error) {
        return (
            <div className={classes.error}>
                <Icon name="warning" className={classes.errorIcon} color={theme.colors.grey[100]} />
                <Text className={classes.errorText}>{t(`error_an_error_occurred`)}</Text>
            </div>
        );
    }

    return (
        <div className={rootClassNames}>
            {showImages && expandImages && (
                <UploadImageCarousel
                    images={images ?? []}
                    limit={6}
                    onDelete={onDeleteImage}
                    onAdd={onAddImage}
                    carouselFullModalClass={carouselFullModalClassNames}
                    isLoading={images === null}
                />
            )}
            <div className={classes.contentContainer}>
                {template?.guid && (
                    <div
                        id={`fm-fragment-${template.guid.toUpperCase()}`}
                        className={classes.fragmentContainer}
                    />
                )}
                {!template && formTypes && (
                    <div className={classes.templateSelect}>
                        <Select
                            label={t('label_select_form_type')}
                            placeholder={t('label_selectone')}
                            onChange={setTemplate}
                            options={formTypes}
                            value={template}
                        />
                    </div>
                )}
                {isLoading && !formTypes && (
                    <div className={classes.loaderOverlay}>
                        <Loader className={classes.loader} />
                    </div>
                )}
            </div>
        </div>
    );
}

FormModal.propTypes = {
    form: PropTypes.object,
    showImages: PropTypes.bool,
    expandImages: PropTypes.bool,
    setExpandImages: PropTypes.func,
    isLoading: PropTypes.bool,
};

export default memo(FormModal);
